/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import {
  Icon,
  type IconName,
  SaleTag,
  Tab,
  TabPanel,
  Tabs,
  Text,
  toast,
  Tooltip
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import {
  DeliveryDateSelect,
  PickUpSelect,
  ProductList
} from '@/Application/Components';
import CommuneStoreAlert from '@/Application/Components/CommuneStoreAlert/CommuneStoreAlert';
import type { PickupItem } from '@/Application/Components/PickUpSelect/PickUpSelect';
import { IntangiblePanel } from '@/Application/Containers/DeliveryCard/IntangiblePanel';
import {
  type DeliveryCardProps,
  ESellerType,
  type TimeFrame
} from '@/Application/Containers/DeliveryCard/interfaces';
import ScheduledDeferred from '@/Application/Containers/DeliveryCard/ScheduledDeferred';
import { useAppSelector } from '@/Application/Hooks/Redux';
import { DatePickerModal, PickupIdModal } from '@/Application/Modals';
import { SelectShopModal } from '@/Application/Modals/SelectShopModal';
import type { IPickupId } from '@/Domain/PickupId';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  getDeliveryGroupsLength,
  getEcommerceCheckout,
  getEcommerceFromGroup
} from '@/modules/shared/utils/gtmEvents/events/utils';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import { isSameDay } from '@/modules/shared/utils/isSameDay';
import type { PickupOption } from '@/modules/shippingMethod/domain/ShippingMethod';
import type {
  GeneralDeliveryOptionDto,
  HttpClientPreferenceParams
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import useSetOnBehalf from '@/sections/cart/hooks/mutators/useSetOnBehalf';
import useGetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useGetGeneralShippingMethods';
import useSetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useSetGeneralShippingMethods';
import useSetTimeFrame from '@/sections/shippingMethods/hooks/useSetTimeFrame';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';

const LABEL_SENT_BY_PARIS = 'Paris.cl';
const LABEL_MARKETPLACE_PRODUCT = 'Producto Marketplace';
const LABEL_SELLER_VALIDATED = 'Vendedor validado por Paris.';
const LABEL_PROVIDER_TEXT =
  'Este producto es vendido por Paris pero será enviado directamente desde la bodega de ';
const LABEL_PRODUCT_SENT_BY = 'Producto enviado por ';
const STORE_NAME_EASY = 'Easy';
const LABEL_EASY_PRODUCT = 'Producto Easy';
const LABEL_EASY_TEXT = 'Vendido por otra tienda Cencosud a través de Paris.';
const LABEL_SELLER_WILL_SHIP = 'El vendedor se hará cargo del envío.';
const LABEL_WAREHOUSE_SHIPPING = 'Nuestra bodega se encargará del envío.';
const COLLECT = 'COLLECT';
const WAREHOUSE = 'WAREHOUSE';
const PROVIDER = 'PROVIDER';
const SHIP_FROM_STORE = 'SHIP_FROM_STORE';
const DROPSHIPPING = 'DROPSHIPPING';
const FULFILLMENT_BY = 'FULFILLMENT_BY';
const SHIPPING = 'despacho';
const PICKUP = 'retiro';

const extractId = (id: string) => {
  const match = id.match(/^delivery-([a-z0-9-]+?)-\d+$/);
  return match ? match[1] : id;
};

const getPickupPriority = (options: PickupOption[] | undefined) => {
  if (!options) return 'IN_STORE';

  const hasStore = options?.some((opt) => opt.shippingSubType === 'IN_STORE');
  const hasCopec = options?.some((opt) => opt.shippingSubType === 'COPEC');

  if (!hasStore) return 'COPEC';
  if (!hasCopec) return 'IN_STORE';
  return 'IN_STORE';
};

const getPickupParams = (
  options: PickupOption[] | undefined
): HttpClientPreferenceParams => {
  const pickupPriority = getPickupPriority(options);
  const newPickupOption = options?.find(
    (option) => option.shippingSubType === pickupPriority
  );

  return {
    shippingType: newPickupOption?.shippingType,
    shippingMethod: newPickupOption?.shippingMethod,
    shippingSubType: newPickupOption?.shippingSubType
  };
};

const DeliveryCard: React.FC<DeliveryCardProps> = ({
  id,
  productList,
  iconName,
  title,
  saleTagLabel,
  saleTagStoreName,
  intangible = false,
  shippingOptions,
  scheduledOptions,
  pickupOptions,
  sellerType,
  cartShippingAddress,
  handleSetOptions,
  handleSelectClientOption,
  setOtherError,
  groupNumber,
  classificationGroupMeta
}) => {
  const onBehalf = useAppSelector((state) => state?.cart?.onBehalf);
  const cart = useAppSelector((state) => state?.cart?.data);
  const { trigger: setOnBehalf, toastMessages } = useSetOnBehalf();

  const selectedScheduledOption = scheduledOptions?.find((opt) => opt.selected);
  const selectedShippingOption = shippingOptions?.find((opt) => opt.selected);
  const selectedPickupOption = pickupOptions?.find((opt) => opt.selected);

  const initialTabActive =
    selectedShippingOption || selectedScheduledOption ? SHIPPING : PICKUP;

  const isSameDayDelivery =
    selectedShippingOption?.shippingMethod === 'SAME_DAY';

  const { setShippingOption } = useSetTimeFrame();
  const [tabActive, setTabActive] = useState(initialTabActive);

  const { setGeneralShippingOption } = useSetGeneralShippingMethods();
  const { shippingMethods } = useGetGeneralShippingMethods();

  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isSelectShopModalOpen, setIsSelectShopModalOpen] = useState(false);
  const [isSelectCopecModalOpen, setIsSelectCopecModalOpen] = useState(false);

  const [pickUp, setPickUp] = useState<string | undefined>(
    selectedPickupOption && selectedPickupOption?.radioButton?.value
      ? selectedPickupOption.radioButton?.value
      : pickupOptions?.[0]?.radioButton?.value
  );

  const [pickupId, setPickupId] = useState<IPickupId | undefined>(undefined);

  const skus = productList
    ?.map((p) => p.productSKU)
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    if (!onBehalf) return;
    const deliveryGroup = onBehalf?.find(
      (group) => group?.deliveryGroupId === id
    );
    if (!deliveryGroup) return;

    // Check new format first
    if (deliveryGroup?.documentType && deliveryGroup?.documentID) {
      setPickupId({
        fullname: deliveryGroup?.name,
        documentType: deliveryGroup?.documentType,
        documentID: deliveryGroup?.documentID
      });
    }
    // Fallback to legacy format
    else if (deliveryGroup?.rut) {
      setPickupId({
        fullname: deliveryGroup?.name,
        documentType: IdentificationDocumentTypes.RUT,
        documentID: deliveryGroup?.rut,
        rut: deliveryGroup?.rut // Keep for compatibility
      });
    }
  }, [onBehalf, id]);

  const sameDayId = isSameDayDelivery
    ? extractId(selectedShippingOption?.id)
    : '';

  const onSelectClientOption =
    (groupId: string) => (param: HttpClientPreferenceParams, date?: string) =>
      handleSelectClientOption(groupId, param, date);

  const [timeFrame, setTimeFrame] = useState<TimeFrame | null>(
    isSameDayDelivery && selectedShippingOption
      ? (() => {
          const matchedChip = selectedShippingOption.chips?.find(
            (chip) =>
              chip?.value?.timeSlot?.name ===
              classificationGroupMeta?.timeSlotIndex
          );

          const selectedChip = matchedChip || selectedShippingOption.chips?.[0];

          return {
            isInitialTimeFrame: true,
            id: sameDayId,
            shipDate: selectedChip?.value.shipDate as string,
            timeSlot: selectedChip?.value.timeSlot
          };
        })()
      : null
  );

  const [isModalDatePickerOpen, setIsModalDatePickerOpen] = useState(false);

  const handleSelectOption = async (
    card: PickupItem,
    type: 'paris' | 'copec'
  ) => {
    if (type === 'paris' && card?.brand === 'copec') {
      const parisOption = cardList?.find(
        (option) => option.shippingSubType === 'IN_STORE'
      );
      if (parisOption) {
        const isSelected = await setGeneralShippingOption({
          deliveryGroupId: id,
          isPickup: true,
          id: parisOption.collectNodeCode
        });

        if (isSelected) {
          setPickUp(parisOption.radioButton?.value);
        } else {
          setOtherError(true);
          toast({
            title: 'Error al realizar el cambio',
            body: 'Por favor, intenta de nuevo.',
            position: 'bottom-left',
            status: 'error'
          });
        }
      }
    } else if (type === 'copec' && card.brand !== 'copec') {
      const copecOption = cardList?.find(
        (option) => option.shippingSubType === 'COPEC'
      );
      if (copecOption) {
        const isSelected = await setGeneralShippingOption({
          deliveryGroupId: id,
          isPickup: true,
          id: copecOption.collectNodeCode
        });

        if (isSelected) {
          setPickUp(copecOption.radioButton?.value);
        } else {
          setOtherError(true);
          toast({
            title: 'Error al realizar el cambio',
            body: 'Por favor, intenta de nuevo.',
            position: 'bottom-left',
            status: 'error'
          });
        }
      }
    }
  };
  const cardList: PickupItem[] | undefined = useMemo(
    () =>
      pickupOptions?.map((card) => ({
        ...card,
        selected: card.selected || false,
        ...(card.cta && {
          cta: {
            ...card.cta,
            onClick: () => {
              chooseAnotherShopEvent(card?.brand || '');
              if (card.brand !== 'copec') {
                setIsSelectShopModalOpen(true);
              } else {
                setIsSelectCopecModalOpen(true);
              }
            }
          }
        }),
        radioButton: card.radioButton
          ? {
              ...card.radioButton,
              onChange: () => {
                if (card.brand === 'copec') {
                  SendShippingEvents.sendEvent({
                    event: 'baseEvent',
                    extraFields: {
                      'event-interactive-accion':
                        ShippingEventInteractiveActions.ClickCopec,
                      'event-interactive-tag': card.title,
                      'event-interactive-grupo_despacho': `${groupNumber} - ${gtmData.totalGroups}`
                    }
                  });
                }
                handleSelectClientOption(id, {
                  shippingMethod: card.shippingMethod,
                  shippingSubType: card.shippingSubType,
                  shippingType: card.shippingType,
                  nodeCode: card.collectNodeCode
                });
              }
            }
          : undefined
      })),
    [pickupOptions]
  );

  const chooseAnotherShopEvent = (shop: string) => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': `Click link elegir otra tienda ${shop}`,
        'event-interactive-accion':
          ShippingEventInteractiveActions.PickupInteractions,
        'event-interactive-grupo_despacho': `${groupNumber} - ${gtmData.totalGroups}`
      }
    });
  };

  const handleDateChange = (optionId: string) => {
    if (tabActive === SHIPPING) {
      const shippingOption = shippingOptions?.find(
        (option) => option.id === optionId
      );
      if (shippingOption) {
        handleSelectClientOption(id, {
          shippingType: shippingOption.shippingType,
          shippingSubType: shippingOption.shippingSubType,
          shippingMethod: shippingOption.shippingMethod,
          deliveryDateFrom: shippingOption.deliveryDateFrom?.split('T')[0],
          deliveryDateTo: shippingOption.deliveryDateTo?.split('T')[0]
        });
      }
    }
  };

  const dateList = shippingOptions?.map((date) => {
    const {
      id,
      shippingType,
      shippingSubType,
      shippingMethod,
      deliveryDateFrom,
      deliveryDateTo
    } = date;
    const deliveryGroupId = extractId(id);
    return {
      ...date,
      selected: shippingOptions.length !== 1 ? date.selected : true,
      onChange: (id: string) => handleDateChange(id),
      chips: date.chips?.map((chip) => {
        const { value } = chip;

        return {
          ...chip,
          onClick: () => {
            if (!value.timeSlot) return;

            handleSelectClientOption(deliveryGroupId, {
              shippingType: shippingType,
              shippingSubType: shippingSubType,
              shippingMethod: shippingMethod,
              deliveryDateFrom: deliveryDateFrom?.split('T')[0],
              deliveryDateTo: deliveryDateTo?.split('T')[0],
              timeSlot: value.timeSlot.name
            });
          },
          selected: value.timeSlot?.name === timeFrame?.timeSlot?.name
        };
      })
    };
  });

  const scheduledDeferredProps = shippingOptions?.map((data) => {
    const { price, crossedOutPrice } = data;
    return {
      title: '¿Cuándo quieres recibir tu compra?',
      firstText: 'Entrega por correo',
      secondText: 'Te enviaremos la fecha de entrega a tu correo:',
      price,
      crossedOutPrice,
      selected: shippingOptions.length !== 1 ? data.selected : true
    };
  })[0];

  if (!selectedShippingOption && tabActive === SHIPPING && dateList?.length) {
    dateList[0].selected = true;
  }

  const handleClickAnotherPerson = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': ShippingEventInteractiveTags.AnotherPerson,
        'event-interactive-accion':
          ShippingEventInteractiveActions.AnotherPerson,
        'event-interactive-grupo_despacho': `${groupNumber} - ${gtmData.totalGroups}`
      }
    });
    setIsPickupModalOpen(true);
  };

  const pickUpProps = {
    deliveryGroupId: id,
    title: '¿Dónde quieres retirar?',
    pickupId,
    onEditPickupId: () => {
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-accion':
            ShippingEventInteractiveActions.AnotherPerson,
          'event-interactive-tag': ShippingEventInteractiveTags.ClickOnPencil,
          'event-interactive-grupo_despacho': `${groupNumber} - ${shippingMethods?.deliveryGroups ? shippingMethods.deliveryGroups.length : 0}`
        }
      });
      setIsPickupModalOpen(true);
    },
    onDeletePickupId: async () => {
      try {
        await setOnBehalf({ deliveryGroupId: id, authorizedPerson: {} });
        setPickupId(undefined);
      } catch (error) {
        console.error('Error removing on behalf:', error);
      }
    },
    button: {
      label: '¿Retira otra persona.?',
      iconName: 'person' as IconName,
      onClick: () => {
        handleClickAnotherPerson();
      }
    },
    cardList
  };

  const handleTabChange = async (option: 'despacho' | 'retiro') => {
    setTabActive(option);
    if (option === 'retiro') {
      const clientPreference = getPickupParams(pickupOptions);
      handleSelectClientOption(id, clientPreference);
    } else {
      handleSelectClientOption(id);
    }
  };

  const findScheduledOption = (
    date: Date
  ): GeneralDeliveryOptionDto | undefined => {
    const options = scheduledOptions && scheduledOptions[0].options;
    return options?.find((opt) =>
      isSameDay(new Date(opt.deliveryDateFrom), date)
    );
  };

  const handleCalendarDateConfirm = async (date: Date) => {
    if (scheduledOptions) {
      const scheduledOption = findScheduledOption(date);
      const shippingOption = scheduledOptions[0];

      handleSelectClientOption(
        id,
        {
          deliveryDateFrom: scheduledOption?.deliveryDateFrom.split('T')[0],
          deliveryDateTo: scheduledOption?.deliveryDateTo.split('T')[0],
          shippingMethod: shippingOption.shippingMethod,
          shippingSubType: shippingOption.shippingSubType,
          shippingType: shippingOption.shippingType
        },
        scheduledOption?.deliveryDateFrom
      );
    }
  };
  useEffect(() => {
    if (
      timeFrame &&
      !timeFrame.isInitialTimeFrame &&
      tabActive === SHIPPING &&
      isSameDayDelivery
    ) {
      setShippingOption(
        { shipDate: timeFrame.shipDate, timeSlot: timeFrame?.timeSlot },
        id
      );
      setTimeFrame((prevValue) => {
        return {
          ...(prevValue as TimeFrame),
          isInitialTimeFrame: true
        };
      });
    }
  }, [timeFrame, isSameDayDelivery, setShippingOption, tabActive]);

  const selectedPickUp = cardList?.find(
    (option) => option.radioButton?.value === pickUp
  );

  const shippingProps = {
    title: '¿Cuándo quieres recibir tu compra?',
    dateList,
    onChooseDate: () => {
      setIsModalDatePickerOpen(true);
    }
  };

  const gtmData = {
    groupNumber,
    totalGroups: getDeliveryGroupsLength(shippingMethods?.deliveryGroups),
    sku: skus
  };

  const deliveryDisabled =
    !shippingOptions?.length && !scheduledOptions?.length;
  const deliveryTabLabel = deliveryDisabled
    ? 'Despacho no disponible'
    : 'Despacho';

  const pickupDisabled = !pickupOptions?.length;
  const pickupTabLabel = pickupDisabled ? 'Retiro no disponible' : 'Retiro';

  let sentTooltipTitle: string | undefined;
  let sentTooltipText: string | undefined;
  let sentBy: string | undefined;

  const soldTooltip = {
    title: '',
    text: ''
  };

  const formatTitle = (str: string): string =>
    str
      .toLowerCase()
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const isSelected = selectedShippingOption?.selected;
  const storeName = formatTitle(
    productList?.[0]?.saleTagProps?.storeName ?? ''
  );
  const originNodeName = formatTitle(
    classificationGroupMeta?.originNodeNameIndex ?? ''
  );
  const originNodeType = selectedShippingOption?.originNodeType;
  const originNodeAdmin = formatTitle(
    selectedShippingOption?.originNodeAdmin ?? ''
  );
  const isWarehouse = originNodeType === WAREHOUSE;
  const isProvider = originNodeType === PROVIDER;
  const isShipFromStore =
    selectedShippingOption?.shippingCapability === SHIP_FROM_STORE;
  const logisticsModel = selectedShippingOption?.logisticsModel;
  const isDropshipping = logisticsModel === DROPSHIPPING;
  const isFulfillmentBy = logisticsModel === FULFILLMENT_BY;
  const isNotEasy = storeName !== STORE_NAME_EASY;
  const isEasy = storeName === STORE_NAME_EASY;

  if (selectedShippingOption?.shippingType === COLLECT) {
    sentBy = undefined;
  } else if (sellerType === ESellerType['1P']) {
    // => PARIS 1P
    if (isSelected && isWarehouse) {
      // => PARIS 1P / BODEGA
      sentBy = LABEL_SENT_BY_PARIS;
    } else if (isSelected && isProvider) {
      // => PARIS 1P / VEV
      sentBy = originNodeAdmin;
      sentTooltipTitle = LABEL_PRODUCT_SENT_BY + originNodeAdmin;
      sentTooltipText = LABEL_PROVIDER_TEXT + originNodeAdmin;
    } else if (isSelected && isShipFromStore) {
      // => PARIS 1P / SHIP FROM STORE
      sentBy = originNodeName ? originNodeName : LABEL_SENT_BY_PARIS;
      sentTooltipTitle = LABEL_PRODUCT_SENT_BY + originNodeAdmin;
      sentTooltipText = LABEL_PROVIDER_TEXT + originNodeAdmin;
    }
  } else if (sellerType === ESellerType['3P']) {
    // => 3P
    if (isSelected && isDropshipping && isNotEasy) {
      // 3P DROPSHIPPING
      sentBy = storeName;
      sentTooltipTitle = LABEL_MARKETPLACE_PRODUCT;
      sentTooltipText = LABEL_SELLER_WILL_SHIP;
      soldTooltip.title = LABEL_MARKETPLACE_PRODUCT;
      soldTooltip.text = LABEL_SELLER_VALIDATED;
    } else if (isSelected && isFulfillmentBy && isNotEasy) {
      // 3P FULFILLMENT BY
      sentBy = LABEL_SENT_BY_PARIS;
      sentTooltipTitle = LABEL_MARKETPLACE_PRODUCT;
      sentTooltipText = LABEL_WAREHOUSE_SHIPPING;
    } else if (isSelected && isEasy) {
      // 3P EASY STORES
      if (isShipFromStore) {
        // => EASY 3P / SHIP FROM STORE
        sentBy = originNodeName ? originNodeName : STORE_NAME_EASY;
        sentTooltipTitle = LABEL_PRODUCT_SENT_BY + originNodeAdmin;
        sentTooltipText = LABEL_PROVIDER_TEXT + originNodeAdmin;
      } else if (!isShipFromStore) {
        // => EASY 3P / BODEGA EASY
        sentBy = storeName;
        soldTooltip.title = LABEL_EASY_PRODUCT;
        soldTooltip.text = LABEL_EASY_TEXT;
      }
    }
  }

  const SentBy = () => {
    const [tooltipSent, setTooltipSent] = useState(false);
    const handleTooltipShown = () => {
      if (tooltipSent) return;

      const tag = sentTooltipTitle?.startsWith(LABEL_PRODUCT_SENT_BY)
        ? 'Enviado por'
        : 'Vendido por';

      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-accion': ShippingEventInteractiveActions.Click,
          'event-interactive-category':
            ShippingEventInteractiveCategories.Checkout,
          'event-interactive-tag': tag
        }
      });

      setTooltipSent(true);
    };

    if (sentBy) {
      if (sentTooltipText || sentTooltipTitle) {
        return (
          <Tooltip title={sentTooltipTitle} content={sentTooltipText}>
            <SaleTag
              label="Enviado por"
              storeName={sentBy}
              iconRight="info-circle"
              onMouseEnter={handleTooltipShown}
            />
          </Tooltip>
        );
      } else {
        return <SaleTag label="Enviado por" storeName={sentBy} />;
      }
    }

    return null;
  };

  const HasOnlyOneScheduleOption = scheduledOptions?.some(
    (opt) => opt.options.length === 1
  );

  const isSelectAnotherDateAvailable =
    !!scheduledOptions?.length && !HasOnlyOneScheduleOption;

  const showCommuneStoreAlert =
    tabActive === PICKUP && !selectedPickUp?.hasCommuneOption && !intangible;

  const isScheduledDeferred =
    selectedShippingOption?.shippingMethod === 'SCHEDULED_DEFERRED';

  useEffect(() => {
    const sendDeliveryEvent = async () => {
      if (!cart) return;

      const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;
      const group = deliveryGroups?.find((group) => group.id === id);

      const selectedDate = group?.optionSelected?.deliveryDateFrom;
      const formattedDate = selectedDate ? selectedDate?.split('T')[0] : '';

      const skusSeparedBySlash = group?.products.map((p) => p).join('/');

      const groupPrice = group?.totalPrice?.centAmount ?? 0;
      const costoDeliveryOption = groupPrice ? groupPrice : '';

      const priceOfProductsInGroup = group?.products?.reduce((acc, product) => {
        const lineItem = cart?.lineItems?.find(
          (item) => item.variant.sku === product
        );

        const itemPrice = lineItem?.price?.value?.centAmount || 0;
        const quantity = lineItem?.quantity || 1;
        const totalItemPrice = itemPrice * quantity;

        return acc + totalItemPrice;
      }, 0);

      if (!skusSeparedBySlash) return;
      if (!priceOfProductsInGroup) return;
      if (!group) return;

      const ecommerce = getEcommerceCheckout(cart);
      const filteredEcommerce = getEcommerceFromGroup(ecommerce, group);

      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-accion': skusSeparedBySlash,
          'event-interactive-category':
            ShippingEventInteractiveCategories.Checkout,
          'event-interactive-tag': formattedDate,
          'event-interactive-costo': costoDeliveryOption,
          'event-interactive-grupo_despacho': `${groupNumber ?? ''}-${deliveryGroups?.length ?? ''}`,
          'event-interactive-value': priceOfProductsInGroup,
          dimension46: costoDeliveryOption,
          ecommerce: filteredEcommerce
        }
      });
    };

    sendDeliveryEvent();
  }, [cart?.deliveryInfo?.deliveryGroups, cart?.lineItems, groupNumber, id]);

  return (
    <>
      <div
        className="co-flex co-flex-col co-w-full co-h-full"
        data-testid="co-delivery-card"
      >
        <div
          className={classNames(
            'co-flex co-justify-between co-w-full',
            'tablet:co-p-4 co-border-b co-border-b-neutral-300',
            'co-flex-col tablet:co-flex-row'
          )}
        >
          <div
            className={classNames(
              'co-flex tablet:co-gap-2 co-items-center',
              'co-p-3 tablet:co-p-0 co-justify-between'
            )}
          >
            <div className="co-flex co-gap-2 co-items-center">
              <Icon name={iconName} className="co-w-8 co-h-8" />
              <Text variant="subtitle2" weight={600}>
                {title}
              </Text>
              <SentBy />
            </div>
            {saleTagLabel && saleTagStoreName && (
              <SaleTag label={saleTagLabel} storeName={saleTagStoreName} />
            )}
          </div>
          <div
            className={classNames(
              'co-flex co-items-center co-p-3',
              'co-border-t co-border-t-neutral-300',
              'tablet:co-border-0 tablet:co-p-0'
            )}
          >
            {!intangible && (
              <Tabs className="co-w-full tablet_w:co-w-auto">
                <Tab
                  active={tabActive === PICKUP}
                  iconName="il-store"
                  label={pickupTabLabel}
                  name={PICKUP}
                  disabled={pickupDisabled}
                  onClick={() => {
                    if (tabActive === PICKUP) return;
                    handleTabChange(PICKUP);
                  }}
                  tagProps={
                    selectedPickUp?.isFree
                      ? {
                          children: 'Gratis'
                        }
                      : undefined
                  }
                />
                <Tab
                  active={tabActive === SHIPPING}
                  iconName="il-delivery"
                  label={deliveryTabLabel}
                  name={SHIPPING}
                  disabled={deliveryDisabled}
                  onClick={() => {
                    if (tabActive === SHIPPING) return;
                    handleTabChange(SHIPPING);
                  }}
                />
              </Tabs>
            )}
          </div>
        </div>

        {showCommuneStoreAlert && (
          <div className="tablet:co-py-2 tablet:co-px-3 tablet:co-gap-[10px]">
            <CommuneStoreAlert />
          </div>
        )}

        <div className="co-flex co-flex-col tablet_w:co-grid tablet_w:co-grid-cols-2 co-h-full">
          {/* Product List */}
          <div className="co-p-4">
            <ProductList
              productList={productList}
              handleSetOptions={handleSetOptions}
              gtmData={gtmData}
              soldTooltip={soldTooltip}
            />
          </div>
          {/* Panels */}
          <div
            className={classNames(
              'co-flex co-flex-col',
              'tablet_w:co-border-l tablet_w:co-border-l-neutral-300',
              'co-border-t co-border-t-neutral-300',
              'tablet_w:co-border-t-0'
            )}
          >
            {isScheduledDeferred && !intangible && (
              <ScheduledDeferred {...scheduledDeferredProps} />
            )}
            {intangible && !isScheduledDeferred && <IntangiblePanel />}
            {!intangible && !isScheduledDeferred && (
              <>
                <TabPanel name={SHIPPING} active={tabActive === SHIPPING}>
                  {shippingOptions && (
                    <DeliveryDateSelect
                      {...shippingProps}
                      isSelectAnotherDateAvailable={
                        isSelectAnotherDateAvailable
                      }
                      gtmData={gtmData}
                    />
                  )}
                </TabPanel>
                <TabPanel name={PICKUP} active={tabActive === PICKUP}>
                  {pickupOptions && (
                    <PickUpSelect
                      {...pickUpProps}
                      onModalOpen={handleSelectOption}
                      selectedCard={pickUp ? pickUp : undefined}
                      gtmData={gtmData}
                      skus={productList
                        ?.map((product) => product.productSKU)
                        .join(',')}
                      showCommuneStoreAlert={showCommuneStoreAlert}
                    />
                  )}
                </TabPanel>
              </>
            )}
          </div>
        </div>
      </div>
      <DatePickerModal
        isOpen={isModalDatePickerOpen}
        onClose={() => {
          setIsModalDatePickerOpen(false);
        }}
        scheduledOptions={scheduledOptions}
        onConfirm={(date) => {
          handleCalendarDateConfirm(date as Date);
        }}
        gtmData={gtmData}
      />
      <PickupIdModal
        isOpen={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        deliveryGroupId={id}
        gtmData={gtmData}
        pickupForm={pickupId}
        customerDocumentInfo={{
          documentType: cart?.shippingAddress?.documentType,
          documentID: cart?.shippingAddress?.documentID,
          customerRut: cart?.shippingAddress?.customerRut
        }}
        onSubmit={(dataPickupId?: IPickupId) => {
          if (dataPickupId) {
            setPickupId(dataPickupId);
            setIsPickupModalOpen(false);

            const toastMessage =
              dataPickupId?.rut || dataPickupId?.documentID
                ? toastMessages?.success
                : toastMessages?.errorEdit;

            SendShippingEvents.sendEvent({
              event: 'baseEvent',
              extraFields: {
                'event-interactive-tag': `${toastMessage?.title}. ${toastMessage?.body}`,
                'event-interactive-accion':
                  ShippingEventInteractiveActions.ResponseAnotherPerson,
                'event-interactive-grupo_despacho': `${groupNumber} - ${gtmData?.totalGroups}`,
                documentType: dataPickupId?.documentType
              }
            });
          }
        }}
      />

      <SelectShopModal
        isOpen={isSelectShopModalOpen}
        onClose={() => setIsSelectShopModalOpen(false)}
        products={productList}
        shopType="paris"
        selectedPickup={selectedPickUp}
        cartShippingAddress={cartShippingAddress}
        gtmData={gtmData}
        setOtherError={setOtherError}
        handleSelectClientOption={onSelectClientOption(id)}
      />
      <SelectShopModal
        isOpen={isSelectCopecModalOpen}
        onClose={() => setIsSelectCopecModalOpen(false)}
        products={productList}
        shopType="copec"
        selectedPickup={selectedPickUp}
        cartShippingAddress={cartShippingAddress}
        gtmData={gtmData}
        setOtherError={setOtherError}
        handleSelectClientOption={onSelectClientOption(id)}
      />
    </>
  );
};

export default DeliveryCard;
